/**
 * Ready Up Node - Player View
 *
 * Full-screen competitive ready check experience.
 * Pulsing button, radial progress, animated player slots.
 * Adapts layout for 2-player duels up to 100-player battle royale.
 */

import type { ReadyUpConfig } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";
import { describeArc } from "./components/svg-utils";

type ReadyUpState = {
  readyPlayers: string[];
  notReadyPlayers: string[];
};

export function ReadyUpView({ nodeState, players, onAction }: NodePlayerViewProps<ReadyUpState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = nodeState.config as ReadyUpConfig;
  const state = nodeState.state as ReadyUpState;
  const isReady = state.readyPlayers.includes(userId || "");

  // Resolve customizable labels: undefined → default, '' → hidden
  const title = config.labels?.title ?? "Match Found";
  const subtitle = config.labels?.subtitle ?? "Confirm you are ready to play";
  const buttonText = config.labels?.buttonText ?? "Ready";

  const handleReady = async () => {
    setIsSubmitting(true);
    setError(null);

    const result = await onAction("ready", { userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  const readyCount = state.readyPlayers.length;
  const totalCount = players.length;
  const progress = totalCount > 0 ? readyCount / totalCount : 0;

  // Radial progress arc (0–360)
  const arcAngle = progress * 359.99;

  // Show individual ticks only when there are few enough players
  const showTicks = totalCount <= 20;

  return (
    <NodeCard>
      <NodeHeader title={title} subtitle={subtitle} />

      {/* Center section: radial ring + button */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="flex justify-center mb-6"
      >
        <div className="relative">
          {/* Radial progress ring */}
          <svg
            aria-hidden="true"
            width="180"
            height="180"
            viewBox="0 0 180 180"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            {/* Background ring */}
            <circle
              cx="90"
              cy="90"
              r="84"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-surface-700/50"
            />
            {/* Progress arc */}
            {arcAngle > 0 && (
              <motion.path
                d={describeArc(90, 90, 84, 0, arcAngle)}
                fill="none"
                stroke="url(#readyGradient)"
                strokeWidth="3"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            )}
            {/* Segment ticks — only for small lobbies */}
            {showTicks &&
              players.map((_, i) => {
                const angle = (i / totalCount) * 360 - 90;
                const rad = (angle * Math.PI) / 180;
                const innerR = 76;
                const outerR = 80;
                const x1 = 90 + innerR * Math.cos(rad);
                const y1 = 90 + innerR * Math.sin(rad);
                const x2 = 90 + outerR * Math.cos(rad);
                const y2 = 90 + outerR * Math.sin(rad);
                return (
                  <line
                    key={i}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="text-surface-600/60"
                  />
                );
              })}
            <defs>
              <linearGradient id="readyGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#26d9b5" />
                <stop offset="100%" stopColor="#3de0c0" />
              </linearGradient>
            </defs>
          </svg>

          {/* Center content */}
          <div className="relative w-[180px] h-[180px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              {!isReady ? (
                <motion.div
                  key="ready-btn"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col items-center"
                >
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-24 h-24 rounded-full bg-brand-500/20 animate-pulse-glow" />
                  </div>
                  <button
                    type="button"
                    onClick={handleReady}
                    disabled={isSubmitting}
                    className={cn(
                      "relative z-10 w-[100px] h-[100px] rounded-full",
                      "bg-gradient-to-b from-brand-400 to-brand-600",
                      "text-surface-900 font-display font-bold text-lg uppercase tracking-wider",
                      "shadow-[0_0_30px_rgba(38,217,181,0.3),0_0_60px_rgba(38,217,181,0.1)]",
                      "hover:shadow-[0_0_40px_rgba(38,217,181,0.5),0_0_80px_rgba(38,217,181,0.2)]",
                      "hover:scale-105 active:scale-95",
                      "transition-all duration-200 cursor-pointer",
                      "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                      "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
                    )}
                  >
                    {isSubmitting ? (
                      <span className="inline-block w-5 h-5 border-2 border-surface-900/30 border-t-surface-900 rounded-full animate-spin" />
                    ) : (
                      buttonText
                    )}
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="ready-confirmed"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <div className="relative">
                    <div className="w-[72px] h-[72px] rounded-full bg-brand-500/10 border-2 border-brand-500/40 flex items-center justify-center">
                      <motion.svg
                        aria-hidden="true"
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="text-brand-400"
                      >
                        <motion.path
                          d="M5 13l4 4L19 7"
                          stroke="currentColor"
                          strokeWidth={2.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.4, delay: 0.2 }}
                        />
                      </motion.svg>
                    </div>
                    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-display uppercase tracking-widest text-brand-400/80 whitespace-nowrap">
                      {buttonText}
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Ready count */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-center mb-4"
      >
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/80 border border-surface-700/50">
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {readyCount}
          </span>
          <span className="text-surface-500 text-sm">/</span>
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {totalCount}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            ready
          </span>
        </div>
      </motion.div>

      <ErrorBanner error={error} />
    </NodeCard>
  );
}
